import * as React from 'react';

import {
  enumCatalyticConverterDetails,
  enumComplianceOptions,
} from '@rematter/modules/trade/types/scaleTickets';
import {
  cn,
  ConsoleTouchDialog,
  Row,
  TouchButton,
  TouchTextField,
} from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import { LucideSparkles } from 'lucide-react';

import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { TouchDialogHeader } from './TouchDialogHeader';

import {
  $catalyticDetailsState,
  $closeCatalyticDetails,
} from '~/modules/trade/_state/materialsInterface.state';
import { useInventoryItem } from '~/modules/trade/hooks/useInventoryItem';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useTheme } from '~/shared/hooks';

type CatalyticConverterDetailsDialogProps = {
  onUpdateValues: (data: {
    id: string;
    serialNumber?: string;
    oemCode?: string;
    compliance?: string;
  }) => void;
  className?: string;
};

//
// ⚛️ ⚛️ ⚛️ EDIT CATALYTIC CONVERTER DETAILS DIALOG ⚛️ ⚛️ ⚛️
//
export const EditCatalyticConverterDetailsDialog = ({
  onUpdateValues,
  className,
}: CatalyticConverterDetailsDialogProps) => {
  // Translations
  const t = useTranslations();
  const catDetails = t.scaleTicketPro.catalyticConverterDetails;

  // States
  const [view, setView] = React.useState(
    enumCatalyticConverterDetails.serialNumber,
  );

  // Atoms
  const [catalyticDetailsState, setCatalyticDetailsState] = useAtom(
    $catalyticDetailsState,
  );
  const closeCatalyticDetails = useSetAtom($closeCatalyticDetails);

  const { isOpen, lineItemId, itemId, serialNumber, oemCode, compliance } =
    catalyticDetailsState;

  // Other data hooks
  const { effectiveColorScheme } = useTheme();
  const inventoryItem = useInventoryItem(itemId);

  const modalStyle = useDialogModalStyle();

  return (
    <ConsoleTouchDialog
      touchModeEnabled={true}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={closeCatalyticDetails}
      className="@EditCatalyticConverterDetailsDialog"
      dialogClassName={cn('h-full w-full max-w-full', className)}
      classNameHeader="!pb-0"
      contentStyle={modalStyle}
      header={
        <TouchDialogHeader
          icon={<LucideSparkles size={16} />}
          iconColor="primary"
          title={inventoryItem?.name ?? ''}
          subtitle={inventoryItem?.itemGroup?.name}
          tabs={Object.values(enumCatalyticConverterDetails).map((option) => ({
            key: option,
            label: catDetails.tabs[enumCatalyticConverterDetails[option]],
          }))}
          selectedTab={view}
          onTabChange={(key) => setView(key as enumCatalyticConverterDetails)}
        />
      }
    >
      {view === enumCatalyticConverterDetails.serialNumber && (
        <TouchTextField
          className="h-30 text-5xl"
          value={serialNumber ?? ''}
          onChange={(v) => {
            setCatalyticDetailsState((prev) => ({ ...prev, serialNumber: v }));
            if (lineItemId)
              onUpdateValues({
                id: lineItemId,
                serialNumber: v ?? undefined,
                oemCode: oemCode ?? undefined,
                compliance,
              });
          }}
          fillLength={17}
          fillChar="0"
          aria-label={catDetails.tabs.serialNumber}
          touchModeEnabled
        />
      )}

      {view === enumCatalyticConverterDetails.oemCode && (
        <TouchTextField
          className="h-30 text-5xl"
          value={oemCode ?? ''}
          onChange={(v) => {
            setCatalyticDetailsState((prev) => ({ ...prev, oemCode: v }));
            if (lineItemId)
              onUpdateValues({
                id: lineItemId,
                serialNumber: serialNumber ?? undefined,
                oemCode: v ?? undefined,
                compliance,
              });
          }}
          placeholder={catDetails.tabs.oemCode}
          aria-label={catDetails.tabs.oemCode}
          touchModeEnabled
        />
      )}

      {view === enumCatalyticConverterDetails.compliance && (
        <Row gap4>
          {Object.values(enumComplianceOptions).map((option) => {
            const label = catDetails.complianceOptions[option];
            return (
              <TouchButton
                key={option}
                isSelected={compliance === option}
                onPress={() => {
                  setCatalyticDetailsState((prev) => ({
                    ...prev,
                    compliance: option,
                  }));
                  if (lineItemId)
                    onUpdateValues({
                      id: lineItemId,
                      serialNumber: serialNumber ?? undefined,
                      oemCode: oemCode ?? undefined,
                      compliance: option,
                    });
                }}
                variant="outline"
                color="neutral"
                className="w-full"
                aria-label={label}
              >
                <div className="flex w-full flex-col items-start justify-start">
                  {label}
                </div>
              </TouchButton>
            );
          })}
        </Row>
      )}
    </ConsoleTouchDialog>
  );
};
